/**
 * IDENTIFY identity screen — "who owns this node?" step of onboarding, per the `sIdentity`
 * screen in the onboarding prototype
 * (docs/superpowers/design-reference/1815-onboarding/onboarding-prototype.dc.html, lines
 * 244-265, id `#cn-identity`... section): an Eyebrow reading "Identify" with a right-aligned
 * green "SAVED AUTOMATICALLY" meta note (line 248), the serif heading "Who owns this node?"
 * (line 250), three labeled text inputs — USERNAME (`#cn-user`, placeholder "ada", helper
 * "Lowercase, permanent — how the node knows you.", lines 252-254), DISPLAY NAME (`#cn-display`,
 * placeholder "Ada Lin", lines 257-258), NODE NAME (`#cn-node`, placeholder "Attic Node",
 * lines 260-262) — and a primary "Continue" button (line 264).
 *
 * Pure presentation: it owns no identity-submission state of its own, it only renders
 * whatever `IdentityState` the kernel-driven flow hands it and emits `submit-identity` for
 * the host to act on.
 *
 * Mirrors `./identify-invitation.ts` and `../../pairing/pairing-view.ts`'s idiom: `extends
 * HTMLElement`, `set state()` -> `#render()`, shadow root + inline `<style>` using
 * `var(--token, <light-literal-fallback>)` so the shell's token sheet (adopted at the
 * `<node-app-shell>` root) drives theming while still standing alone if this element is ever
 * mounted outside that tree, plus the same reduced-motion/forced-colors guards. Not
 * self-registered here (like the mirrored files) — the host and the test file each register
 * the tag with a `customElements.get` guard.
 *
 * The username field is live-normalized to lowercase `[a-z0-9-]` as the user types (a real
 * `input` listener strips everything else immediately), matching the "Lowercase, permanent"
 * helper copy. Continue validates all three fields are non-empty client-side before emitting
 * `submit-identity` — an empty required field shows an inline `role="alert"` message instead
 * and leaves focus on the form (nothing is disabled by a validation failure, only by
 * `working`).
 */
function normalizeUsername(value) {
    return value.toLowerCase().replace(/[^a-z0-9-]/g, "");
}
export class NodeOnboardingIdentifyIdentityElement extends HTMLElement {
    #state = {};
    #validationError;
    set state(value) {
        this.#state = value;
        this.#validationError = undefined;
        this.#render();
    }
    get state() {
        return this.#state;
    }
    connectedCallback() {
        this.#render();
    }
    #render() {
        const root = this.shadowRoot ?? this.attachShadow({ mode: "open" });
        root.replaceChildren();
        const style = document.createElement("style");
        style.textContent = `
      :host { display: block; font-family: var(--ui-font, Inter, system-ui, sans-serif); color: var(--ink-hi, #0A0A0A); }
      section {
        display: flex; flex-direction: column; gap: 16px;
        padding: clamp(20px, 6vw, 36px) clamp(16px, 5vw, 32px);
        background: var(--bg-raised, #FFFFFF);
        border: 1px solid var(--ui-line-soft, rgba(0, 0, 0, 0.06));
        border-radius: var(--ui-radius-lg, 14px);
        box-shadow: var(--ui-shadow-1, 0 1px 1px rgba(10, 10, 10, 0.05), 0 10px 24px -14px rgba(10, 10, 10, 0.25));
      }
      .top-row { display: flex; align-items: center; justify-content: space-between; }
      .eyebrow {
        font: 600 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .saved-automatically {
        font: 500 9.5px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: light-dark(var(--c-green-deep, #2B7258), var(--c-green, #36AA75));
      }
      h1 {
        margin: 0; font: 400 clamp(1.75rem, 6vw, 2.125rem)/1.05 var(--ui-serif, "Instrument Serif", serif);
      }
      .field { display: flex; flex-direction: column; gap: 6px; }
      .field label {
        font: 400 10px/1 var(--ui-mono, "JetBrains Mono", ui-monospace, monospace);
        letter-spacing: 0.14em; text-transform: uppercase;
        color: var(--ink-low, #888888);
      }
      .field input {
        min-height: 2.375rem; box-sizing: border-box;
        border: 1px solid var(--hr-strong, rgba(10, 10, 10, 0.12)); border-radius: var(--ui-radius, 10px);
        padding: .5rem .75rem; color: var(--ink-hi, #0A0A0A); background: var(--bg-0, #FFFFFF);
        font: 400 .9375rem/1.5 var(--ui-font, Inter, system-ui, sans-serif);
      }
      .field input::placeholder { color: var(--ink-mid, #545454); }
      #cn-user { font-family: var(--ui-mono, "JetBrains Mono", ui-monospace, monospace); }
      .helper { margin: 0; font-size: 11.5px; color: var(--ink-xlo, #B5B5B5); }
      [role=alert] {
        margin: 0; font-size: 12.5px; line-height: 1.5;
        color: light-dark(var(--c-coral-deep, #B85551), var(--c-coral, #DF6662));
      }
      .continue {
        min-height: 2.5rem; border: 1px solid var(--ink-hi, #0A0A0A); border-radius: var(--ui-radius, 10px);
        padding: .6rem 1rem; font: 500 .875rem/1.2 var(--ui-font, Inter, system-ui, sans-serif);
        color: var(--bg-0, #FFFFFF); background: var(--ink-hi, #0A0A0A); cursor: pointer; width: 100%;
        transition: background 0.18s var(--ease-out, ease-out), border-color 0.18s var(--ease-out, ease-out);
      }
      .continue:hover:not(:disabled) { background: var(--c-coral-deep, #B85551); border-color: var(--c-coral-deep, #B85551); }
      .continue:disabled { opacity: 0.6; cursor: default; }
      .continue:focus-visible, .field input:focus-visible {
        outline: 2px solid var(--ds-accent, #D4635F); outline-offset: 2px;
      }
      @media (prefers-reduced-motion: reduce) {
        * { scroll-behavior: auto !important; transition-duration: 0.01ms !important; animation: none !important; }
      }
      @media (forced-colors: active) {
        section, .field input, .continue { border: 1px solid CanvasText; }
      }
    `;
        const section = document.createElement("section");
        const topRow = document.createElement("div");
        topRow.className = "top-row";
        const eyebrow = document.createElement("p");
        eyebrow.className = "eyebrow";
        eyebrow.textContent = "Identify";
        const savedAutomatically = document.createElement("span");
        savedAutomatically.className = "saved-automatically";
        savedAutomatically.textContent = "SAVED AUTOMATICALLY";
        topRow.append(eyebrow, savedAutomatically);
        const heading = document.createElement("h1");
        heading.textContent = "Who owns this node?";
        const { username = "", displayName = "", nodeName = "", error, working = false } = this.#state;
        const usernameField = this.#buildField({
            id: "cn-user",
            label: "USERNAME",
            placeholder: "ada",
            value: username,
            working,
            helper: "Lowercase, permanent — how the node knows you.",
            normalize: normalizeUsername,
        });
        const displayField = this.#buildField({
            id: "cn-display",
            label: "DISPLAY NAME",
            placeholder: "Ada Lin",
            value: displayName,
            working,
        });
        const nodeField = this.#buildField({
            id: "cn-node",
            label: "NODE NAME",
            placeholder: "Attic Node",
            value: nodeName,
            working,
        });
        const displayedError = this.#validationError ?? error;
        let alert;
        if (displayedError !== undefined) {
            alert = document.createElement("p");
            alert.setAttribute("role", "alert");
            alert.setAttribute("aria-live", "assertive");
            alert.textContent = displayedError;
        }
        const continueButton = document.createElement("button");
        continueButton.type = "button";
        continueButton.className = "continue";
        continueButton.textContent = "Continue";
        continueButton.disabled = working;
        continueButton.addEventListener("click", () => {
            const submittedUsername = normalizeUsername(usernameField.input.value.trim());
            const submittedDisplayName = displayField.input.value.trim();
            const submittedNodeName = nodeField.input.value.trim();
            if (submittedUsername === "" || submittedDisplayName === "" || submittedNodeName === "") {
                this.#validationError = "Username, display name, and node name are all required.";
                this.#render();
                return;
            }
            this.#validationError = undefined;
            this.dispatchEvent(new CustomEvent("submit-identity", {
                bubbles: true,
                composed: true,
                detail: {
                    username: submittedUsername,
                    displayName: submittedDisplayName,
                    nodeName: submittedNodeName,
                },
            }));
        });
        section.append(topRow, heading, usernameField.field, displayField.field, nodeField.field);
        if (alert !== undefined)
            section.append(alert);
        section.append(continueButton);
        root.append(style, section);
    }
    #buildField(options) {
        const field = document.createElement("div");
        field.className = "field";
        const label = document.createElement("label");
        label.htmlFor = options.id;
        label.textContent = options.label;
        const input = document.createElement("input");
        input.type = "text";
        input.id = options.id;
        input.placeholder = options.placeholder;
        input.value = options.value;
        input.spellcheck = false;
        input.autocomplete = "off";
        input.disabled = options.working;
        field.append(label, input);
        let helperElement;
        if (options.helper !== undefined) {
            helperElement = document.createElement("span");
            helperElement.className = "helper";
            helperElement.id = `${options.id}-helper`;
            helperElement.textContent = options.helper;
            input.setAttribute("aria-describedby", helperElement.id);
            field.append(helperElement);
        }
        if (options.normalize !== undefined) {
            const normalize = options.normalize;
            input.addEventListener("input", () => {
                const normalized = normalize(input.value);
                if (normalized !== input.value)
                    input.value = normalized;
            });
        }
        return { field, input };
    }
}
//# sourceMappingURL=identify-identity.js.map